/**
 * Check if IndexedDB is supported in the current environment.
 * @returns {boolean} True if IndexedDB is available.
 */
export function isIndexedDBSupported() {
    try {
        return typeof indexedDB !== "undefined" && typeof indexedDB.open === "function";
    } catch {
        return false;
    }
}

const MATRIX_STORAGE_PREFIX = "dfm_matrix_";
const INSTALLATION_ID_KEY = `${MATRIX_STORAGE_PREFIX}installation_id`;
const RECOVERY_PENDING_KEY = `${MATRIX_STORAGE_PREFIX}recovery_pending`;
const LEGACY_CRYPTO_OWNER_KEY = `${MATRIX_STORAGE_PREFIX}legacy_crypto_owner`;
const LEGACY_CRYPTO_DATABASE_PREFIX = "matrix-js-sdk";
export function getMatrixInstallationId(userUuid) {
    const userInstallationId = userUuid && localStorage.getItem(`${INSTALLATION_ID_KEY}_${userUuid}`);
    if (userInstallationId) {
        return userInstallationId;
    }
    let installationId = localStorage.getItem(INSTALLATION_ID_KEY);
    if (!installationId) {
        installationId = typeof crypto?.randomUUID === "function"
            ? crypto.randomUUID()
            : `${Date.now()}-${crypto.getRandomValues(new Uint32Array(4)).join("-")}`;
        localStorage.setItem(INSTALLATION_ID_KEY, installationId);
    }
    return installationId;
}

// A fresh browser without credentials may encounter an orphaned server registration.
// Isolate only this account; preserve other accounts and all existing crypto stores.
export function renewMatrixInstallationId(userUuid) {
    const installationId = crypto.randomUUID();
    localStorage.setItem(`${INSTALLATION_ID_KEY}_${userUuid}`, installationId);
    return installationId;
}

export function getMatrixBinding(userUuid) {
    if (!userUuid) {
        return null;
    }
    try {
        const storedBinding = JSON.parse(localStorage.getItem(`${MATRIX_STORAGE_PREFIX}binding_${userUuid}`));
        if (
            typeof storedBinding?.userId !== "string" ||
            typeof storedBinding?.deviceId !== "string" ||
            typeof storedBinding?.proxyToken !== "string"
        ) {
            return null;
        }
        return storedBinding;
    } catch {
        return null;
    }
}

export function saveMatrixBinding(userUuid, session, cryptoStoreGeneration) {
    if (!userUuid) {
        return;
    }
    localStorage.setItem(
        `${MATRIX_STORAGE_PREFIX}binding_${userUuid}`,
        JSON.stringify({
            userId: session.userId,
            deviceId: session.deviceId,
            proxyToken: session.token,
            cryptoStoreGeneration,
        })
    );
}

export function savePendingMatrixBindingMigration(userUuid, session, cryptoStoreGeneration) {
    if (!userUuid) {
        return;
    }
    localStorage.setItem(
        `${MATRIX_STORAGE_PREFIX}binding_${userUuid}`,
        JSON.stringify({
            userId: session.userId,
            deviceId: session.deviceId,
            proxyToken: session.token,
            cryptoStoreGeneration,
            cryptoStoreMigrationPending: true,
        })
    );
}

export function completeMatrixBindingMigration(userUuid, session, cryptoStoreGeneration, cryptoDatabasePrefix) {
    if (cryptoDatabasePrefix === LEGACY_CRYPTO_DATABASE_PREFIX) {
        localStorage.setItem(LEGACY_CRYPTO_OWNER_KEY, `${session.userId}:${session.deviceId}`);
    }
    saveMatrixBinding(userUuid, session, cryptoStoreGeneration);
}

export function markMatrixBindingUnusable(userUuid, deviceId) {
    const binding = getMatrixBinding(userUuid);
    if (binding?.deviceId === deviceId) {
        localStorage.setItem(
            `${MATRIX_STORAGE_PREFIX}binding_${userUuid}`,
            JSON.stringify({ ...binding, unusable: true })
        );
    }
}

export function removeMatrixBinding(userUuid) {
    localStorage.removeItem(`${MATRIX_STORAGE_PREFIX}binding_${userUuid}`);
}

export function markMatrixRecoveryPending(userUuid) {
    if (userUuid) {
        localStorage.setItem(`${RECOVERY_PENDING_KEY}_${userUuid}`, "1");
    }
}

export function isMatrixRecoveryPending(userUuid) {
    return Boolean(userUuid && localStorage.getItem(`${RECOVERY_PENDING_KEY}_${userUuid}`));
}

export function clearMatrixRecoveryPending(userUuid) {
    if (userUuid) {
        localStorage.removeItem(`${RECOVERY_PENDING_KEY}_${userUuid}`);
    }
}

export function getCryptoDatabasePrefix(userId, deviceId, allowUnclaimedLegacyStore) {
    const legacyOwner = localStorage.getItem(LEGACY_CRYPTO_OWNER_KEY);
    if (legacyOwner === `${userId}:${deviceId}` || (!legacyOwner && allowUnclaimedLegacyStore)) {
        return LEGACY_CRYPTO_DATABASE_PREFIX;
    }
    return `matrix-js-sdk-${encodeURIComponent(userId)}-${encodeURIComponent(deviceId)}`;
}

export function getScopedMatrixStorageKey(key, matrixClient) {
    const userId = matrixClient?.getUserId?.();
    const deviceId = matrixClient?.getDeviceId?.();
    if (!userId || !deviceId) {
        return null;
    }
    return `${MATRIX_STORAGE_PREFIX}${key}_${encodeURIComponent(userId)}_${encodeURIComponent(deviceId)}`;
}

export function clearNonMatrixLocalStorage() {
    const matrixStorage = preserveMatrixStorage();
    localStorage.clear();
    restoreMatrixStorage(matrixStorage);
}

export function preserveMatrixStorage() {
    return Object.fromEntries(
        Object.entries(localStorage).filter(([key]) => key.startsWith(MATRIX_STORAGE_PREFIX))
    );
}

export function restoreMatrixStorage(values) {
    Object.entries(values).forEach(([key, value]) => localStorage.setItem(key, value));
}
